import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, Phone, Repeat } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import DumpsterCard from "../components/DumpsterCard";
import ReviewCarousel from "../components/ReviewCarousel";
import FAQAccordion from "../components/FAQAccordion";
import CTASection from "../components/CTASection";
import { useRequestModal } from "../components/RequestModal";
import { BUSINESS, ROLLOFF_SIZES, SERVICE_AREAS } from "../data/site";
import { track } from "../lib/api";

export default function RollOff() {
  const { openRequest } = useRequestModal();
  const heroVideoRef = useRef(null);
  useEffect(() => {
    const v = heroVideoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);
  return (
    <>
      <SEO
        title="Roll-Off Dumpster Rentals"
        description="Roll-off dumpster rentals in Prescott, Prescott Valley & across Yavapai County. 20, 30 & 40 yard sizes. Same-Day Swaps Available. No hidden fees."
        path="/roll-off-dumpsters"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Roll-Off Dumpster Rental",
          provider: { "@type": "LocalBusiness", name: BUSINESS.name, telephone: BUSINESS.phone },
          areaServed: SERVICE_AREAS.map((a) => a.name),
        }}
      />

      {/* Hero */}
      <section className="bg-ink text-white" data-testid="rolloff-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pb-20">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Roll-Off Dumpsters" }]} dark />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
                Roll-Off Dumpster Rentals in <span className="text-sky-400">Yavapai County</span>
              </h1>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed">
                20, 30, and 40 yard roll-offs for contractors, construction sites, and one-time projects. No maximum rental period — keep it as long as the job requires.
              </p>
              <div className="mt-8 hidden sm:flex flex-col sm:flex-row gap-4">
                <button onClick={() => openRequest()} className="px-8 py-4 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-heading font-semibold transition-all active:scale-[0.98]" data-testid="rolloff-hero-request">
                  Request a Dumpster
                </button>
                <a href={BUSINESS.phoneHref} onClick={() => track("click_to_call", { location: "rolloff_hero" })} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border-2 border-slate-600 hover:border-sky-400 font-heading font-semibold transition-all" data-testid="rolloff-hero-call">
                  <Phone className="h-4 w-4" /> {BUSINESS.phone}
                </a>
              </div>
              <ul className="mt-8 hidden sm:flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Repeat className="h-4 w-4 text-sky-400" /> Same-Day Swaps Available</li>
                <li className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-sky-400" /> Saturday Swaps Available</li>
                <li className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-sky-400" /> No Hidden Fees</li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 bg-sky-600/10 rounded-2xl -rotate-1" aria-hidden="true" />
              <video
                ref={heroVideoRef}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/assets/yws/uploads/rolloff-video-poster.jpg"
                className="relative rounded-2xl w-full object-cover aspect-[16/10] shadow-2xl"
                data-testid="rolloff-hero-video"
                aria-label="YWS roll-off dumpsters and truck in action"
              >
                <source src="/assets/yws/uploads/rolloff-hero-video.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Sizes */}
      <section className="py-16 sm:py-20 bg-white" data-testid="rolloff-sizes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Choose Your Size</h2>
            <Link to="/dumpster-size-guide" className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-sky-700 hover:text-sky-800 underline underline-offset-4" data-testid="rolloff-compare-link">
              Compare Dumpster Sizes <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
            {ROLLOFF_SIZES.map((s, i) => (
              <Reveal key={s.size} delay={i * 0.08}>
                <DumpsterCard {...s} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-slate-700 text-sm sm:text-base">
                <span className="font-heading font-bold text-slate-900">Not sure what size you need?</span> Tell us what you're working on and we'll help you choose.
              </p>
              <button onClick={() => openRequest("Not Sure — Help Me Choose")} className="shrink-0 px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-heading font-semibold text-sm transition-all active:scale-[0.98]" data-testid="rolloff-help-me-choose">
                Help Me Choose
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How rentals work */}
      <section className="py-16 sm:py-20 bg-slate-50 border-y border-slate-200" data-testid="rolloff-how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight text-center">How Rentals Work</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "1", title: "Request", text: "Pick a size online or call. Not sure? We'll help you choose." },
              { n: "2", title: "Confirm", text: "We confirm placement and straightforward pricing. No surprise fees." },
              { n: "3", title: "Load", text: "Keep it as long as the job requires — no maximum rental period." },
              { n: "4", title: "Swap or Pull", text: "Call when it's full or done. Same-Day Swaps Available; Saturday Swaps Available." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="bg-white rounded-xl border border-slate-200 p-6 h-full">
                  <span className="font-heading font-extrabold text-4xl text-sky-600/25" aria-hidden="true">{s.n}</span>
                  <h3 className="font-heading font-bold text-lg text-slate-900 mt-2 mb-1.5">{s.title}</h3>
                  <p className="text-sm text-slate-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6">
            <p className="text-center text-sm text-slate-500 max-w-2xl mx-auto">
              Not sure if your material can go in the dumpster? Give us a call before loading — certain materials may require special handling or may not be accepted.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contractor benefits */}
      <section className="py-16 sm:py-20 bg-ink text-white" data-testid="rolloff-contractor">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Contractor & High-Volume Accounts</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Multiple job sites. Frequent swaps. Long-term rentals. This is exactly what we're built for — with pricing structured around how you actually use us. The more you use us, the better we can work with you.
            </p>
            <Link to="/contractor-dumpster-rentals#pricing" className="mt-7 inline-flex px-7 py-3.5 rounded-lg bg-sky-600 hover:bg-sky-500 font-heading font-semibold transition-all active:scale-[0.98]" data-testid="rolloff-contractor-cta">
              Get Contractor Pricing
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="font-heading font-bold text-lg mb-3">One-Time or Homeowner Rental?</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                You don't need to be a contractor to rent from YWS. Cleanouts, remodels, roofing, yard projects — we deliver the same reliable service and straightforward pricing to every customer.
              </p>
              <button onClick={() => openRequest()} className="mt-5 inline-flex px-6 py-3 rounded-lg border-2 border-slate-500 hover:border-sky-400 font-heading font-semibold text-sm transition-all" data-testid="rolloff-homeowner-cta">
                Request a Dumpster
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <ReviewCarousel />

      <section className="py-16 sm:py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion page="rolloff" />
        </div>
      </section>

      {/* Service areas */}
      <section className="py-14 bg-white border-t border-slate-200" data-testid="rolloff-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl text-slate-900">Roll-Off Delivery Across Yavapai County</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {SERVICE_AREAS.map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:border-sky-500 hover:text-sky-700 transition-colors" data-testid={`rolloff-area-${a.slug}`}>
                {a.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection title="Get Your Roll-Off on the Schedule" />
    </>
  );
}
